import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminJobs, createJob, updateJob, deleteJob, type Job } from './api';
import './Admin.css';

export default function AdminCareers() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  
  // Field States
  const [department, setDepartment] = useState('Logistics');
  const [workplaceType, setWorkplaceType] = useState('On-site');
  const [employmentType, setEmploymentType] = useState('Full-Time');
  const [experienceLevel, setExperienceLevel] = useState('Entry');
  const [city, setCity] = useState('Milton Keynes');
  const [currency, setCurrency] = useState('GBP');
  const [noticePeriod, setNoticePeriod] = useState('1 Month');
  const [minEducation, setMinEducation] = useState('High School');
  
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [requirements, setRequirements] = useState('');
  const [preferred, setPreferred] = useState('');
  const [benefits, setBenefits] = useState('');
  
  // Set default expiry to 30 days from now
  const [expiryDate, setExpiryDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  
  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminJobs();
      setJobs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const resetForm = () => {
    setEditingJobId(null);
    setTitle('');
    setTagline('');
    setSalaryMin('');
    setSalaryMax('');
    setResponsibilities('');
    setRequirements('');
    setPreferred('');
    setBenefits('');
    setDepartment('Logistics');
    setWorkplaceType('On-site');
    setEmploymentType('Full-Time');
    setExperienceLevel('Entry');
    setCity('Milton Keynes');
    setCurrency('GBP');
    setNoticePeriod('1 Month');
    setMinEducation('High School');
    
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setExpiryDate(d.toISOString().split('T')[0]);
  };

  const handleEdit = (job: Job) => {
    setEditingJobId(job._id);
    setTitle(job.title);
    setTagline(job.tagline);
    setSalaryMin(job.salaryMin || '');
    setSalaryMax(job.salaryMax || '');
    setResponsibilities(job.responsibilities);
    setRequirements(job.requirements);
    setPreferred(job.preferred || '');
    setBenefits(job.benefits || '');
    setDepartment(job.department);
    setWorkplaceType(job.workplaceType);
    setEmploymentType(job.employmentType);
    setExperienceLevel(job.experienceLevel);
    setCity(job.city);
    setCurrency(job.currency);
    setNoticePeriod(job.noticePeriod);
    setMinEducation(job.minEducation);
    setExpiryDate(new Date(job.expiryDate).toISOString().split('T')[0]);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await deleteJob(id);
      loadJobs();
    } catch (err: any) {
      alert(err.message || 'Failed to delete');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      title, tagline, salaryMin, salaryMax, responsibilities, requirements, preferred, benefits,
      department, workplaceType, employmentType, experienceLevel, city, currency, noticePeriod, minEducation,
      expiryDate: new Date(expiryDate).toISOString()
    };
    
    try {
      if (editingJobId) {
        await updateJob(editingJobId, payload);
      } else {
        await createJob(payload);
      }
      setIsFormOpen(false);
      resetForm();
      loadJobs();
    } catch (err: any) {
      alert(err.message || 'Failed to save job');
    }
  };

  if (loading) return <p style={{ padding: '20px' }}>Loading jobs...</p>;
  if (error) return <p style={{ padding: '20px', color: 'red' }}>{error}</p>;

  return (
    <div className="admin-page-container">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Manage Careers</h1>
        <button className="admin-btn admin-btn-primary" onClick={() => { resetForm(); setIsFormOpen(true); }}>
          Create New Job
        </button>
      </div>

      {isFormOpen && (
        <div className="admin-card" style={{ marginBottom: '20px' }}>
          <h2>{editingJobId ? 'Edit Job' : 'Create New Job'}</h2>
          <form onSubmit={handleSubmit} className="admin-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            
            {/* Free Text Fields */}
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Job Title *</span>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} required placeholder="e.g. Senior Logistics Manager" />
            </label>
            
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Role Summary / Tagline *</span>
              <input type="text" value={tagline} onChange={e => setTagline(e.target.value)} required placeholder="A brief, engaging summary of the role" />
            </label>
            
            {/* Dropdowns */}
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Department</span>
              <select value={department} onChange={e => setDepartment(e.target.value)}>
                <option>Logistics</option>
                <option>Warehouse Operations</option>
                <option>Customer Support</option>
                <option>Sales & Marketing</option>
                <option>IT & Engineering</option>
                <option>Management</option>
              </select>
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Workplace Type</span>
              <select value={workplaceType} onChange={e => setWorkplaceType(e.target.value)}>
                <option>On-site</option>
                <option>Remote</option>
                <option>Hybrid</option>
              </select>
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Employment Type</span>
              <select value={employmentType} onChange={e => setEmploymentType(e.target.value)}>
                <option>Full-Time</option>
                <option>Part-Time</option>
                <option>Contract</option>
                <option>Internship</option>
              </select>
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Experience Level</span>
              <select value={experienceLevel} onChange={e => setExperienceLevel(e.target.value)}>
                <option>Entry</option>
                <option>Mid</option>
                <option>Senior</option>
                <option>Lead</option>
                <option>Manager</option>
              </select>
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>City / Base Location</span>
              <input type="text" value={city} onChange={e => setCity(e.target.value)} required placeholder="e.g. Milton Keynes" />
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Currency</span>
              <select value={currency} onChange={e => setCurrency(e.target.value)}>
                <option>GBP</option>
                <option>USD</option>
                <option>EUR</option>
              </select>
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Salary Min</span>
              <input type="number" value={salaryMin} onChange={e => setSalaryMin(e.target.value)} placeholder="e.g. 30000" />
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Salary Max</span>
              <input type="number" value={salaryMax} onChange={e => setSalaryMax(e.target.value)} placeholder="e.g. 45000" />
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Notice Period</span>
              <select value={noticePeriod} onChange={e => setNoticePeriod(e.target.value)}>
                <option>Immediate</option>
                <option>15 Days</option>
                <option>1 Month</option>
                <option>2 Months</option>
                <option>3 Months</option>
              </select>
            </label>
            
            <label className="admin-field">
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Minimum Education</span>
              <select value={minEducation} onChange={e => setMinEducation(e.target.value)}>
                <option>High School</option>
                <option>Bachelor's Degree</option>
                <option>Master's Degree</option>
                <option>Diploma</option>
                <option>Any</option>
              </select>
            </label>
            
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Expiry Date (Auto-Delete after this date) *</span>
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} required />
            </label>

            {/* Long Text */}
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Responsibilities *</span>
              <textarea value={responsibilities} onChange={e => setResponsibilities(e.target.value)} rows={4} required placeholder="List key responsibilities..."></textarea>
            </label>
            
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Requirements (Must-have) *</span>
              <textarea value={requirements} onChange={e => setRequirements(e.target.value)} rows={4} required placeholder="List mandatory skills or qualifications..."></textarea>
            </label>
            
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Preferred (Nice-to-have)</span>
              <textarea value={preferred} onChange={e => setPreferred(e.target.value)} rows={3} placeholder="List optional skills or advantages..."></textarea>
            </label>
            
            <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <span style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Perks & Benefits</span>
              <textarea value={benefits} onChange={e => setBenefits(e.target.value)} rows={3} placeholder="List perks like medical, bonuses, remote work options..."></textarea>
            </label>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '15px', marginTop: '10px', borderTop: '1px solid #1e293b', paddingTop: '20px' }}>
              <button type="submit" className="admin-btn admin-btn-primary" style={{ padding: '12px 30px', fontSize: '1.1rem' }}>
                {editingJobId ? 'Update Job' : 'Save Job'}
              </button>
              <button type="button" className="admin-btn admin-btn-secondary" style={{ padding: '12px 30px', fontSize: '1.1rem' }} onClick={() => setIsFormOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Department</th>
              <th>Location</th>
              <th>Expiry</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr><td colSpan={5} style={{textAlign: 'center'}}>No jobs found.</td></tr>
            ) : (
              jobs.map(job => (
                <tr key={job._id}>
                  <td><strong>{job.title}</strong></td>
                  <td>{job.department}</td>
                  <td>{job.city}</td>
                  <td>{new Date(job.expiryDate).toLocaleDateString()}</td>
                  <td>
                    <button className="admin-btn admin-btn-secondary" style={{ marginRight: '8px' }} onClick={() => handleEdit(job)}>Edit</button> <Link to={`/admin/careers/${job._id}/applications`} className="admin-btn admin-btn-ghost" style={{ marginRight: "8px" }}>View Applicants</Link>
                    <button className="admin-btn admin-btn-danger" onClick={() => handleDelete(job._id)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
